import './TableOfContents.scss';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ReactComponent as ChevronDown } from 'images/icons/ChevronDownThin.svg';
import { ReactComponent as ListIcon } from 'images/icons/List.svg';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import { TableOfContentsEntry } from './TableOfContentsEntry/TableOfContentsEntry';
import { Chapter, ContentsEntryType, ModuleTableOfContents } from 'types/learningHub/moduleContents';
import { useServices } from 'index';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

type TableOfContentsProps = {
  contents: ModuleTableOfContents;
  handleEntrySelection: (entryId: string, type: ContentsEntryType, isCompleted: boolean, chapterId?: string) => void;
  isInteractive: boolean;
  selectedEntryId?: string;
  customClasses?: string;
};

export const TableOfContents = ({
  contents,
  selectedEntryId,
  isInteractive,
  handleEntrySelection,
  customClasses,
}: TableOfContentsProps) => {
  const service = useServices().profileService;
  const { data, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: service.checkIfUserIsMember,
    staleTime: Infinity,
  });
  const { t } = useTranslation();

  const [expandedPanelIds, setExpandedPanelIds] = useState<string[]>([contents.chapters[0].id]);

  const toggleSinglePanel = (panelId: string) => () => {
    if (expandedPanelIds.includes(panelId)) {
      setExpandedPanelIds(expandedPanelIds.filter((id) => id !== panelId));
    } else {
      setExpandedPanelIds([...expandedPanelIds, panelId]);
    }
  };

  const toggleAllPanels = () => {
    if (expandedPanelIds.length !== 0) {
      setExpandedPanelIds([]);
    } else {
      const panelIdsToOpen: string[] = contents.chapters.map((chapter) => chapter.id);
      setExpandedPanelIds([...panelIdsToOpen]);
    }
  };

  return (
    <div className={`table-of-contents ${customClasses ?? ''}`}>
      <div className='header'>
        <div>
          <ListIcon />
          <span>{t('Contents', { ns: 'learningHub' })}</span>
        </div>
        <span onClick={toggleAllPanels}>{expandedPanelIds.length === 0 ? 'Expand' : 'Collapse'} all</span>
      </div>
      <div className='contents'>
        <div className='chapters'>
          {contents &&
            contents.chapters.map((chapter, index) => (
              <Panel
                key={`panel-${index}`}
                index={index}
                isExpanded={expandedPanelIds.includes(chapter.id)}
                chapter={chapter}
                isUserMember={data || false}
                isInteractive={isInteractive}
                selectedEntryId={selectedEntryId ?? ''}
                togglePanel={toggleSinglePanel(chapter.id)}
                handleEntrySelection={(id: string, type: ContentsEntryType, isCompleted: boolean, chapterId: string) => handleEntrySelection(id, type, isCompleted, chapterId)}
              />
            ))}
        </div>

        {contents && contents.certifications.length > 0 && (
          <div className='certifications'>
            <div>
              <Medal />
              <span>{t('Certification', { ns: 'learningHub' })}</span>
            </div>

            {contents.certifications.map((certification, index) => (
              <TableOfContentsEntry
                key={`certification-${index}`}
                title={certification?.title}
                isLocked={certification?.status === 'Locked'}
                isCompleted={certification?.status === 'Completed'}
                isPremiumUser={data || false}
                type='certificate'
                comingSoon={certification.status === 'ComingSoon'}
                isSelected={selectedEntryId === certification.id}
                isInteractive={isInteractive}
                handleEntrySelection={() => handleEntrySelection(certification.id, 'certificate', certification?.status === 'Completed')}
              ></TableOfContentsEntry>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

type PanelProps = {
  index: number;
  isExpanded: boolean;
  chapter: Chapter;
  isUserMember: boolean;
  isInteractive: boolean;
  selectedEntryId: string;
  togglePanel: () => void;
  handleEntrySelection: (entryId: string, entryType: ContentsEntryType, isCompleted: boolean, chapterId: string) => void;
};

const Panel = ({
  index,
  isExpanded,
  chapter,
  isUserMember,
  isInteractive,
  selectedEntryId,
  togglePanel,
  handleEntrySelection,
}: PanelProps) => {
  return (
    <>
      <Accordion expanded={isExpanded} onChange={togglePanel}>
        <AccordionSummary expandIcon={<ChevronDown />} aria-controls={`chapter-${index + 1}-content`} id={`chapter-${index}`}>
          <span className='chapter-title'>
            {index + 1}. {chapter?.title}
          </span>
        </AccordionSummary>
        <AccordionDetails>
          <div className='chapter-content'>
            {chapter.lessons.map((lesson, index) => (
              <TableOfContentsEntry
                key={`lesson-${index}`}
                title={lesson?.title}
                isLocked={false}
                isCompleted={lesson?.isCompleted}
                isPremiumUser={isUserMember}
                type='lesson'
                isSelected={selectedEntryId === lesson.id}
                isInteractive={isInteractive}
                handleEntrySelection={() => handleEntrySelection(lesson.id, 'lesson', lesson?.isCompleted, chapter.id)}
              ></TableOfContentsEntry>
            ))}
            {chapter.chapterTest ? (
              <TableOfContentsEntry
                key={`chapterTest-${index}`}
                title={chapter.chapterTest.title}
                isLocked={chapter.chapterTest.status === 'Locked'}
                isCompleted={chapter.chapterTest.status === 'Completed'}
                isPremiumUser={isUserMember}
                type='chapterTest'
                isInteractive={isInteractive}
                isSelected={selectedEntryId === chapter.chapterTest.id}
                handleEntrySelection={() => handleEntrySelection(chapter.chapterTest.id, 'chapterTest', chapter.chapterTest.status === 'Completed', chapter.id)}
              ></TableOfContentsEntry>
            ) : (
              <></>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
